import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Menu, X, LogOut, Moon, Sun, Monitor, CloudUpload, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { NAV_GROUPS } from './nav.js';
import { useAuth } from '../../lib/auth.jsx';
import { useTheme } from '../../lib/theme.js';
import { useOfflineQueue } from '../../hooks/useOfflineQueue.js';
import vjLogo from '../../assets/vj-logo.png';

function Logo() {
  return (
    <span className="flex items-center gap-2">
      <img src={vjLogo} alt="" className="h-9 w-auto shrink-0" aria-hidden="true" />
      <span className="font-display text-lg font-semibold tracking-tight">
        Vijay <span className="text-primary">Jewellery</span>
      </span>
    </span>
  );
}

function NavList({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-4 px-3">
      {NAV_GROUPS.map((group, i) => (
        <div key={group.label ?? `g${i}`} className="flex flex-col gap-0.5">
          {group.label ? (
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted/70">{group.label}</p>
          ) : null}
          {group.items.map((item) => {
            const Icon = item.icon;
            if (item.disabled) {
              return (
                <span
                  key={item.to}
                  className="flex h-touch cursor-not-allowed items-center gap-3 rounded-card px-3 text-sm text-muted/60"
                  title="Coming in a later phase"
                >
                  <Icon size={18} aria-hidden="true" />
                  {item.label}
                </span>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  clsx(
                    'flex h-touch items-center gap-3 rounded-card px-3 text-sm font-medium transition-colors',
                    isActive ? 'bg-primary-soft text-primary' : 'text-content hover:bg-surface-2',
                  )
                }
              >
                <Icon size={18} aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const options = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
    { value: 'system', icon: Monitor, label: 'System' },
  ];
  return (
    <div className="inline-flex rounded-card border border-border bg-surface p-1" role="group" aria-label="Theme">
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setPreference(value)}
          aria-pressed={preference === value}
          title={label}
          className={clsx(
            'flex h-9 w-9 items-center justify-center rounded transition-colors',
            preference === value ? 'bg-primary text-primary-fg' : 'text-muted hover:bg-surface-2 hover:text-content',
          )}
        >
          <Icon size={15} aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}

function SyncBadge() {
  const { queuedCount, isSyncing, syncNow } = useOfflineQueue();
  if (!queuedCount) return null;

  return (
    <button
      type="button"
      onClick={syncNow}
      disabled={isSyncing}
      className="flex h-9 items-center gap-1.5 rounded-card border border-warning/40 bg-warning/10 px-3 text-xs font-medium text-warning disabled:opacity-70"
      title="Drafts saved on this device, waiting to upload"
    >
      {isSyncing ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <CloudUpload size={14} aria-hidden="true" />}
      {queuedCount} waiting to sync
    </button>
  );
}

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <div className="min-h-dvh bg-bg lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-surface lg:flex lg:flex-col">
        <div className="flex h-16 items-center border-b border-border px-5">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavList />
        </div>
        <div className="border-t border-border p-4">
          <p className="truncate text-sm font-medium">{user?.fullName}</p>
          <p className="truncate text-xs text-muted">{user?.deviceLabel}</p>
          <button
            type="button"
            onClick={logout}
            className="mt-3 flex h-10 items-center gap-2 text-sm text-muted hover:text-danger"
          >
            <LogOut size={15} aria-hidden="true" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 flex w-72 flex-col bg-surface shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <Logo />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2"
                aria-label="Close menu"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4">
              <NavList onNavigate={() => setDrawerOpen(false)} />
            </div>
            <div className="border-t border-border p-4">
              <p className="truncate text-sm font-medium">{user?.fullName}</p>
              <p className="truncate text-xs text-muted">{user?.deviceLabel}</p>
              <button
                type="button"
                onClick={logout}
                className="mt-3 flex h-10 items-center gap-2 text-sm text-muted hover:text-danger"
              >
                <LogOut size={15} aria-hidden="true" /> Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-surface px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2 lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <div className="lg:hidden">
              <Logo />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SyncBadge />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppShell;
